import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Shell } from "../../app/Shell";
import { ErrorNotice } from "../../components/ui";
import { localDayEnd, localDayStart } from "../dateRange";
import { ORDERS_PAGE_SIZE, useOrdersList } from "../useOrders";
import { OrdersTable } from "./OrdersTable";
import { OrdersToolbar } from "./OrdersToolbar";
import "../orders.css";

/**
 * Orders: the shop's operational record of what it has sold.
 *
 * It reads the committed admin orders endpoint through the shared API client —
 * there is no second client and no second order type — and every narrowing it
 * offers is applied by the server. The four states a real list has are all real
 * here: loading, empty, failed with a retry, and populated. None is a
 * placeholder, and a failure is never allowed to render as an empty shop —
 * those look identical to an operator and mean opposite things.
 *
 * WHY THE SEARCH TERM IS NOT IN THE URL, WHEN THE PRODUCT LIST PUTS ITS OWN
 * THERE
 * Because this one is a customer's name or phone number. The products screen
 * keeps its search in the address bar so a filtered view can be shared or
 * reloaded, and that is right for a product name. Doing the same here would
 * write a customer's personal details into browser history, into whatever a
 * shared link is pasted into, and into any proxy that records the address of a
 * page — for the convenience of surviving a reload. The status and the date
 * range are operational facts about the shop and stay in the URL; the term an
 * operator typed lives only as long as the screen does.
 */
export function OrdersPage() {
  const [params, setParams] = useSearchParams();

  // Deliberately NOT from the URL. See the note above.
  const [search, setSearch] = useState("");

  const status = params.get("status") ?? "";
  const createdFrom = params.get("created_from") ?? "";
  const createdTo = params.get("created_to") ?? "";
  const page = Number(params.get("page") ?? "1") || 1;

  // The URL keeps the calendar days the operator picked, because that is what
  // they chose and what a shared link should reproduce. What goes to the
  // backend is those days converted into instants IN THE OPERATOR'S OWN
  // TIMEZONE — the same timezone the table renders every timestamp in, so the
  // day on screen is the day being filtered.
  const query = {
    search: search || undefined,
    status: status || undefined,
    created_from: createdFrom ? localDayStart(createdFrom) : undefined,
    created_before: createdTo ? localDayEnd(createdTo) : undefined,
    page,
    page_size: ORDERS_PAGE_SIZE,
  };
  const orders = useOrdersList(query);
  const data = orders.data;

  /** Any change to what is being asked restarts paging: page three of the old
   *  result set is meaningless against a new one, and asking for it is how a
   *  narrowed search comes back empty when it had matches. */
  function update(next: Record<string, string>) {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value) merged.set(key, value);
      else merged.delete(key);
    }
    if (!("page" in next)) merged.delete("page");
    setParams(merged, { replace: true });
  }

  function goToPage(next: number) {
    update({ page: String(next) });
  }

  function changeSearch(next: string) {
    setSearch(next.trim());
    // The term is not a URL parameter, so paging has to be reset by hand here
    // rather than falling out of `update`.
    const merged = new URLSearchParams(params);
    merged.delete("page");
    setParams(merged, { replace: true });
  }

  const narrowed = Boolean(search || status || createdFrom || createdTo);

  return (
    <Shell title="Orders">
      <div className="stack">
        <OrdersToolbar
          search={search}
          status={status}
          createdFrom={createdFrom}
          createdTo={createdTo}
          total={data ? data.total : null}
          onSearch={changeSearch}
          onStatus={(value) => update({ status: value })}
          onCreatedFrom={(value) => update({ created_from: value })}
          onCreatedTo={(value) => update({ created_to: value })}
          onClear={() => {
            setSearch("");
            setParams(new URLSearchParams(), { replace: true });
          }}
        />

        {/* Retry sits beside the message rather than inside it, so the alert an
            assistive reader announces is the sentence and not the button. */}
        {orders.isError ? (
          <div className="orders__failure">
            <ErrorNotice error={orders.error} />
            <button
              type="button"
              className="btn"
              disabled={orders.isFetching}
              onClick={() => void orders.refetch()}
            >
              {orders.isFetching ? "Retrying…" : "Retry"}
            </button>
          </div>
        ) : null}

        <div className="panel">
          {orders.isPending ? (
            <div className="empty" role="status">
              Loading orders…
            </div>
          ) : orders.isError ? null : data && data.orders.length === 0 ? (
            <div className="empty">
              {narrowed
                ? "No orders match these filters."
                : "No orders yet. They appear here as customers place them."}
            </div>
          ) : data ? (
            <OrdersTable items={data.orders} />
          ) : null}
        </div>

        {data && data.total_pages > 1 ? (
          <div className="row" aria-label="Pagination">
            <button
              type="button"
              className="btn btn--sm"
              disabled={page <= 1}
              onClick={() => goToPage(page - 1)}
            >
              Previous
            </button>
            <span className="muted num" role="status">
              Page {data.page} of {data.total_pages}
            </span>
            <button
              type="button"
              className="btn btn--sm"
              disabled={page >= data.total_pages}
              onClick={() => goToPage(page + 1)}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>
    </Shell>
  );
}
